import React, { useState } from "react";
import { Instagram, Facebook, Youtube, Music2, MessageCircle } from "lucide-react";
import { useSettings } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import BenovaLogo from "./BenovaLogo";

export default function Footer() {
  const { settings } = useSettings();
  const { t } = useLang();
  const nav = useNavigate();
  const [clicks, setClicks] = useState(0);

  const social = settings.social || {};

  // Hidden admin access: triple-click on the logo
  const onLogoClick = () => {
    const next = clicks + 1;
    setClicks(next);
    if (next >= 3) {
      setClicks(0);
      nav("/val-benova-admin-2026/login");
    }
    setTimeout(() => setClicks(0), 1500);
  };

  const socials = [
    { key: "instagram", url: social.instagram, icon: Instagram, label: "Instagram" },
    { key: "facebook", url: social.facebook, icon: Facebook, label: "Facebook" },
    { key: "tiktok", url: social.tiktok, icon: Music2, label: "TikTok" },
    { key: "youtube", url: social.youtube, icon: Youtube, label: "YouTube" },
    { key: "whatsapp", url: social.whatsapp ? `https://wa.me/${social.whatsapp.replace(/\D/g,'')}` : "", icon: MessageCircle, label: "WhatsApp" },
  ].filter((s) => !!s.url);

  return (
    <footer id="contacto" className="relative mt-20 border-t" style={{ borderColor: "var(--bn-border)", background: "var(--bn-surface)" }} data-testid="footer">
      <div className="bn-container px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <button
              onClick={onLogoClick}
              className="block outline-none"
              data-testid="footer-logo-hidden-admin"
              aria-label="Benova"
            >
              <BenovaLogo color="var(--bn-text)" accent="var(--bn-accent)" size="xl" />
            </button>
            <p className="mt-6 bn-body-font text-base max-w-md" style={{ color: "var(--bn-text-muted)" }}>
              {t("footer.tagline")}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="bn-eyebrow mb-4">{t("footer.nav")}</div>
            <ul className="space-y-3 bn-body-font">
              <li><a href="/" className="hover:opacity-70" style={{ color: "var(--bn-text)" }}>{t("nav.home")}</a></li>
              <li><a href="/experiencias" className="hover:opacity-70" style={{ color: "var(--bn-text)" }}>{t("nav.experiences")}</a></li>
              <li><a href="#contacto" className="hover:opacity-70" style={{ color: "var(--bn-text)" }}>{t("nav.contact")}</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="bn-eyebrow mb-4">{t("footer.contact")}</div>
            <ul className="space-y-3 bn-body-font" style={{ color: "var(--bn-text)" }}>
              {settings.contact_email && <li>{settings.contact_email}</li>}
              {settings.contact_phone && <li>{settings.contact_phone}</li>}
              {settings.contact_whatsapp && (
                <li>
                  <a href={`https://wa.me/${settings.contact_whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="underline underline-offset-4">
                    WhatsApp: {settings.contact_whatsapp}
                  </a>
                </li>
              )}
            </ul>

            {socials.length > 0 && (
              <div className="mt-8 flex items-center gap-4">
                {socials.map((s) => (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:-translate-y-1"
                    style={{ border: "1px solid var(--bn-border)", color: "var(--bn-text)" }}
                    data-testid={`footer-social-${s.key}`}
                  >
                    <s.icon size={16} />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bn-divider my-12" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>© {new Date().getFullYear()} Benova Travel Group — Riviera Maya</div>
          <div className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>{t("footer.made_in")}</div>
        </div>
      </div>
    </footer>
  );
}
